
// #region Imports

// Vue
import { Component, Prop, Vue } from 'vue-property-decorator'

// Components
import Tags from '@/components/bookings/Tags.vue'

// Interfaces
import { ClientInterface } from '@/types/interfaces/client'

import {
  BookingPropertyInterface,
  ReservationInterface
} from '@/types/interfaces/booking'

// Assets
import countryPrefixes from '@/assets/CountryPrefix'

// Utils & Validation
import rules from '@/utils/rules.utils'
import { sanitizePhoneNumber } from '@/utils/sanitation.utils'

// Services
import settingService from '@/services/setting.service'
import { jsonToString } from '@/utils/helper.utils'
import { PublicBookingCustomVisualSettingsInterface } from '@/types/interfaces/setting'

// #endregion

@Component({ components: { Tags }, methods: { sanitizePhoneNumber } })
export default class BookClientInformation extends Vue {
  @Prop({ default: null }) readonly item!: ReservationInterface
  @Prop({ default: null }) readonly companyId!: string
  @Prop({ default: null, required: false }) readonly submitButtonTitle!: string
  @Prop({ default: false })
  readonly visualSettings!: PublicBookingCustomVisualSettingsInterface

  prefixes = countryPrefixes
  rules = rules

  data = {
    country_code: '+47'
  } as ClientInterface

  tags = []
  selectedTags = { key: 'tags', value: [] } as BookingPropertyInterface

  valid = false

  _sanitizePhoneNumber (value: string): string {
    return sanitizePhoneNumber(value)
  }

  async created (): Promise<void> {
    await this.getTags()
    this.injectCustomCss()
  }

  injectCustomCss (): void {
    if (this.canIApplyVisualSettings) {
      this.$nextTick(() => {
        this.injectInputsCss()
      })
    }
  }

  injectInputsCss (): void {
    // v-input__slot
    const vInput = document.querySelectorAll('div.v-input__slot')
    for (let i = 0; i < vInput.length; i++) {
      const element = vInput[i] as HTMLElement

      // set elements color style and make it important:
      element.style.background = 'var(--v-gCardBackgroundColor-base)'
      element.style.stroke = 'var(--v-gGeneralElementsBackgroundColor-base)'
    }

    const vInputPart = document.querySelectorAll('input')
    for (let i = 0; i < vInputPart.length; i++) {
      const element = vInputPart[i] as HTMLElement

      // set elements color style and make it important:
      element.style.color = 'var(--v-gFontColor-base)'
    }

    // after selection
    const label = document.querySelectorAll('label')
    for (let i = 0; i < label.length; i++) {
      const element = label[i] as HTMLElement

      // set elements color style and make it important:
      element.style.color = 'var(--v-gFontColor-base)'
    }

    // the selected element
    const selectedPart = document.querySelectorAll(
      'div.v-select__selection.v-select__selection--comma'
    )

    for (let i = 0; i < selectedPart.length; i++) {
      const element = selectedPart[i] as HTMLElement

      // set elements color style and make it important:
      element.style.color = 'var(--v-gFontColor-base)'
    }

    const vInputIconPart = document.querySelectorAll('i')
    for (let i = 0; i < vInputIconPart.length; i++) {
      const element = vInputIconPart[i] as HTMLElement

      // set elements color style and make it important:
      element.style.color = 'var(--v-gFontColor-base)'
    }

    const vTextArea = document.querySelectorAll('textarea')
    for (let i = 0; i < vTextArea.length; i++) {
      const element = vTextArea[i] as HTMLElement

      // set elements color style and make it important:
      element.style.color = 'var(--v-gFontColor-base)'
    }
  }

  getText (data: { name: string; code: string }): string {
    return `${data.name} (${data.code})`
  }

  toggleTag (indx: number): void {
    const tempTag = this.tags[indx]
    const localIndx = this.selectedTags.value.indexOf(tempTag)

    if (Array.isArray(this.selectedTags.value)) {
      if (localIndx === -1) {
        this.selectedTags.value.push(tempTag)
        return
      }

      this.selectedTags.value.splice(localIndx, 1)
    }
  }

  async getTags (): Promise<void> {
    const tags = await settingService.getValuesPublic(this.companyId, 'tags')
    this.tags = tags.value
  }

  submit (): void {
    const form = this.$refs.form as HTMLFormElement

    const valid = form.validate()

    if (this.selectedTags.value.length > 0) {
      this.data.tags = {
        ...this.selectedTags,
        value: jsonToString(this.selectedTags.value)
      }
    }

    if (valid) {
      this.$emit('clientInformationAdded', this.data)
    }
  }

  // #region computed properties
  get submitButtonTitleComputed (): string {
    return this.submitButtonTitle || this.$t('common.next').toString()
  }

  get isLoggedIn (): boolean {
    return this.$store.getters['auth/getUser']
  }

  get canIApplyVisualSettings (): boolean {
    return this.visualSettings.active && !this.isLoggedIn
  }

  get elementColor (): string {
    if (this.canIApplyVisualSettings) {
      return 'gGeneralElementsBackgroundColor gGeneralElementsBackgroundColor--text'
    }

    return ''
  }

  get buttonColor (): string {
    return this.canIApplyVisualSettings
      ? 'gButtonBackgroundColor gButtonFontColor--text'
      : 'primary'
  }
  // #endregion
}
